import React, { useEffect, useState } from "react";
import { X, FolderPlus, Loader2, Pencil } from "lucide-react";
import { validateFolderName } from "../../utils/utilFunctions";

export type FolderFormMode = "create" | "rename";

interface FolderFormModalProps {
  isOpen: boolean;
  mode: FolderFormMode;
  initialName?: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void> | void;
  isLoading?: boolean;
}

const FolderFormModal: React.FC<FolderFormModalProps> = ({
  isOpen,
  mode,
  initialName = "",
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setError("");
    }
  }, [isOpen, initialName]);

  if (!isOpen) return null;

  const isRename = mode === "rename";
  const trimmedName = name.trim();
  const isUnchanged = isRename && trimmedName === initialName.trim();
  const submitDisabled = !trimmedName || isLoading || isUnchanged;

  const handleSubmit = async () => {
    if (submitDisabled) return;
    await onSubmit(trimmedName);
  };

  const handleChange = (value: string) => {
    if (!validateFolderName(value)) {
      setError("Only alphabets, numbers, and spaces are allowed");
      return;
    }
    setError("");
    setName(value);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-full max-w-md animate-in zoom-in-95 duration-200">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="pt-12 p-6">
            <div className="flex items-center justify-between pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  {isRename ? (
                    <Pencil className="w-5 h-5 text-blue-600" />
                  ) : (
                    <FolderPlus className="w-5 h-5 text-blue-600" />
                  )}
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {isRename ? "Rename Folder" : "Create New Folder"}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                type="button"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Folder Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => handleChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSubmit();
                  }}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all"
                  placeholder="Enter folder name"
                  autoFocus
                  maxLength={100}
                />
                {error && (
                  <p className="text-red-500 text-sm mt-1">{error}</p>
                )}
              </div>
            </div>

            <div className="mt-8 flex gap-3 justify-end">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitDisabled}
                className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm flex items-center gap-2"
                type="button"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isRename ? "Rename" : "Create Folder"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FolderFormModal;
